import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GET_LINKED_ACCOUNTS_URL } from "../../app/api/apiRoutes";
import { LinkedAccount } from "../../app/models/LinkedAccount";
import { State } from "../../app/store";
import useApiRequest from "./useApiRequest";
import useFacility from "./useFacility";
import { getLinkedAccountDisplayName } from "../linkedAccountDisplay";
import getId from "../getId";

/** Wartość selecta „wszyscy / bez filtra” — brak `accountIds` w URL. */
export const MY_ACCOUNT_ACCOUNT_FILTER_ALL = "";

export function useLinkedAccountsOptions() {
  const allowSubaccounts = useSelector((s: State) => s.config.configuration.allowSubaccounts === true);
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(GET_LINKED_ACCOUNTS_URL(facilityIds));
      if (res?.isError) {
        setAccounts([]);
        return;
      }
      const raw = res?.result;
      setAccounts(Array.isArray(raw) ? (raw as LinkedAccount[]) : []);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [facilityIds, get]);

  useEffect(() => {
    if (!allowSubaccounts) {
      setAccounts([]);
      return;
    }
    void fetchAccounts();
  }, [allowSubaccounts, fetchAccounts]);

  const options = useMemo(() => {
    const all = { value: MY_ACCOUNT_ACCOUNT_FILTER_ALL, label: "Wszyscy" };
    if (!allowSubaccounts) {
      return [all];
    }
    return [
      all,
      ...accounts.map((acc) => ({
        value: getId(acc),
        label: getLinkedAccountDisplayName(acc),
      })),
    ];
  }, [allowSubaccounts, accounts]);

  return { options, loading, allowSubaccounts, refetch: fetchAccounts };
}
